"use client";

import { Capacitor } from "@capacitor/core";
import { HiiveInstants } from "./instants-plugin";

export const isNativeInstantsRuntime = () => Capacitor.isNativePlatform();

export async function setInstantsScreenProtected(protectedScreen: boolean) {
  if (!Capacitor.isNativePlatform()) return;
  window.dispatchEvent(
    new CustomEvent("hiive:screen-protection", { detail: protectedScreen }),
  );
  await HiiveInstants.setScreenProtected({ protected: protectedScreen });
}

/** Reuse the privacy owner so protected instant content can never be captured. */
export async function captureNativeViewport(): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    return (await HiiveInstants.captureViewport()).dataUrl;
  } catch {
    // Older binaries and protected screens retain the background fallback.
    return null;
  }
}

export async function getNativeInstantCapabilities() {
  if (!Capacitor.isNativePlatform()) return { torch: false };
  try {
    return await HiiveInstants.getCapabilities();
  } catch {
    return { torch: false };
  }
}

export async function setNativeInstantTorch(enabled: boolean) {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    await HiiveInstants.setTorch({ enabled });
    return true;
  } catch {
    return false;
  }
}

export async function openInstantNativeSettings() {
  if (!Capacitor.isNativePlatform()) return;
  await HiiveInstants.openSettings();
}

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("Read failed"));
    reader.onload = () => {
      const value = String(reader.result ?? "");
      resolve(value.slice(value.indexOf(",") + 1));
    };
    reader.readAsDataURL(blob);
  });

export async function saveInstantImage(input: {
  url: string;
  fileName: string;
  mimeType?: string | null;
}) {
  const response = await fetch(input.url);
  if (!response.ok) throw new Error("Could not load image");
  const blob = await response.blob();

  if (Capacitor.isNativePlatform()) {
    const base64 = await blobToBase64(blob);
    await HiiveInstants.saveImage({
      base64,
      mimeType: input.mimeType || blob.type || "image/jpeg",
      fileName: input.fileName,
    });
    return;
  }

  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = input.fileName;
  anchor.click();
  URL.revokeObjectURL(objectUrl);
}
